import type { PeriodoValor } from '@/shared/types/filtroPeriodo';
import { intervaloOrdenado } from '@/shared/utils/periodoFiltro';

import type {
  AtalhoDeSituacao,
  ConsultaAssinaturas,
  CorpoDaExportacao,
  FiltrosDeAssinaturas,
  FormatoDoArquivo,
  SituacaoDaParticipacao,
} from '../types/negociacaoAssinaturasTypes';

/** O mesmo tamanho de página da Lista de negociações. */
export const TAMANHO_DA_PAGINA = 10;
export const PARTICIPACOES_POR_PAGINA = 20;

/** Teto do core para `casoIds` (validator da consulta e do export). */
export const LIMITE_DE_SELECAO = 100;

/** O mesmo intervalo da busca da Lista de negociações, para a visão e para as opções do filtro. */
export const ESPERA_DA_BUSCA_MS = 350;

/** Opções por lista no Mais filtros: o default do core para `GET /negociacao/assinaturas/opcoes`. */
const LIMITE_DE_OPCOES = 20;

/** O validator das opções recusa busca com mais de 100 caracteres. */
const TAMANHO_MAXIMO_DA_BUSCA_DE_OPCOES = 100;

export const FILTROS_VAZIOS: FiltrosDeAssinaturas = {
  rotuloIn: [],
  representacaoIn: [],
  situacaoIn: [],
  status: [],
  tipo: [],
  temperatura: [],
};

const ATALHOS_DE_UMA_SITUACAO: readonly AtalhoDeSituacao[] = ['NA_VEZ', 'AGUARDANDO', 'A_CONFERIR'];

/**
 * Atalho e filtro "Situação da assinatura" são a mesma escolha (decisão "atalhos e cartões"): o
 * atalho ativo é lido de `situacaoIn`. Com mais de uma situação marcada no Mais filtros, nenhum
 * atalho fica ativo.
 */
export function atalhoDaSituacao(situacaoIn: SituacaoDaParticipacao[]): AtalhoDeSituacao | null {
  if (situacaoIn.length === 0) return 'TODOS';
  if (situacaoIn.length > 1) return null;
  return ATALHOS_DE_UMA_SITUACAO.find((atalho) => atalho === situacaoIn[0]) ?? null;
}

export function situacaoDoAtalho(atalho: AtalhoDeSituacao): SituacaoDaParticipacao[] {
  return atalho === 'TODOS' ? [] : [atalho];
}

export function temBuscaOuFiltro(
  buscaAplicada: string,
  filtros: FiltrosDeAssinaturas,
  periodo: PeriodoValor,
): boolean {
  return (
    buscaAplicada.length > 0 ||
    Boolean(periodo.inicio) ||
    Object.values(filtros).some((valores) => valores.length > 0)
  );
}

export const naoVazio = <T>(valores: T[] | undefined): T[] | undefined =>
  valores && valores.length > 0 ? valores : undefined;

/** O Período é um dia civil de São Paulo em `YYYY-MM-DD`; `periodoFim` sem início é recusado. */
export function periodoDaConsulta(
  periodo: PeriodoValor,
): Pick<ConsultaAssinaturas, 'periodoInicio' | 'periodoFim'> {
  const intervalo = intervaloOrdenado(periodo);
  if (!intervalo) return {};
  const [periodoInicio, periodoFim] = intervalo;
  return { periodoInicio, periodoFim };
}

/** Tira as chaves vazias: o validator do core recusa array vazio e string vazia. */
export function semVazios(consulta: ConsultaAssinaturas): ConsultaAssinaturas {
  return Object.fromEntries(
    Object.entries(consulta).filter(
      ([, valor]) =>
        valor !== undefined && valor !== '' && !(Array.isArray(valor) && valor.length === 0),
    ),
  ) as ConsultaAssinaturas;
}

export function consultaDoDetalhe(casoId: string): ConsultaAssinaturas {
  return { escopo: 'detalhe', casoIds: [casoId] };
}

/** Com seleção, o arquivo abrange só as negociações marcadas, dentro da mesma consulta (CA-12). */
export function montarCorpoDaExportacao(
  consulta: ConsultaAssinaturas,
  formato: FormatoDoArquivo,
  casoIdsSelecionados: string[] = [],
): CorpoDaExportacao {
  const { page: _page, pageSize: _pageSize, ...filtro } = consulta;
  const casoIds = casoIdsSelecionados.length > 0 ? casoIdsSelecionados : filtro.casoIds;
  return { ...semVazios({ ...filtro, casoIds }), format: formato };
}

/** Arrays saem como `campo[]` repetido, como a listagem de negociações já manda. */
export function queryDaConsulta(consulta: ConsultaAssinaturas): string {
  const params = new URLSearchParams();
  Object.entries(consulta).forEach(([campo, valor]) => {
    if (valor === undefined) return;
    if (Array.isArray(valor)) {
      valor.forEach((item) => params.append(`${campo}[]`, String(item)));
      return;
    }
    params.append(campo, String(valor));
  });
  return params.toString();
}

/** Busca vazia sai da query: o validator do core recusa `busca` com menos de 1 caractere. */
export function queryDasOpcoesDeFiltro(busca: string, page = 1): string {
  const params = new URLSearchParams();
  const termo = busca.trim().slice(0, TAMANHO_MAXIMO_DA_BUSCA_DE_OPCOES);
  if (termo) params.append('busca', termo);
  params.append('limit', String(LIMITE_DE_OPCOES));
  if (page > 1) params.append('page', String(page));
  return params.toString();
}
